import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from '../components/Layout';


export default function CarGroup() {

  const navigate = useNavigate();

  // ── Sidebar ──
  const [sideOpen,   setSideOpen]   = useState(true);
  const [activePage, setActivePage] = useState('Car Group');
  const [user,       setUser]       = useState(null);

  // ── Table ──
  const [data,       setData]       = useState([]);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);
  const [search,     setSearch]     = useState('');
  const [loading,    setLoading]    = useState(false);

  // ── Form ──
  const [name,       setName]       = useState('');
  const [sequence,   setSequence]   = useState(1);
  const [editId,     setEditId]     = useState(null);
  const [saving,     setSaving]     = useState(false);

  // ── Auth ──
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }
    setUser(JSON.parse(localStorage.getItem('user') || 'null'));
  }, []);

  // ── Fetch ──
  const fetchData = useCallback((pg = page, q = search) => {
    setLoading(true);
    API.get('/cargroup', { params: { page: pg, limit: 10, search: q } })
      .then(r => {
        setData(r.data.data);
        setTotalPages(r.data.totalPages);
        setTotal(r.data.total);
      })
      .catch(() => toast.error('Failed to load data'))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { fetchData(page, search); }, [page]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchData(1, search); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // ── Add / Update ──
  const handleSubmit = async () => {
    if (!name.trim()) { toast.warning('Enter Car Group name'); return; }
    setSaving(true);
    try {
      if (editId) {
        await API.put(`/cargroup/${editId}`, { name: name.trim(), sequence });
        toast.success('Updated successfully');
      } else {
        await API.post('/cargroup', { name: name.trim(), sequence });
        toast.success('Added successfully');
      }
      setName(''); setSequence(1); setEditId(null);
      fetchData(page, search);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Operation failed');
    }
    setSaving(false);
  };

  // ── Edit ──
  const handleEdit = (row) => {
    setName(row.group_name);
    setSequence(row.sequence);
    setEditId(row.group_id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Cancel ──
  const handleCancel = () => { setName(''); setSequence(1); setEditId(null); };

  // ── Delete ──
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this Car Group?')) return;
    try {
      await API.delete(`/cargroup/${id}`);
      toast.success('Deleted successfully');
      fetchData(page, search);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  const logout = () => { localStorage.clear(); navigate('/login'); };

  return (
    <Layout activePage="Car Group">
  
      <ToastContainer position="top-right" autoClose={3000} />
  
      {/* ── Main Content (UNCHANGED DESIGN) ── */}
      <div style={S.header}>
        <h2 style={S.title}>Car Group Management</h2>
        <p style={S.subtitle}>Add and manage car groups</p>
      </div>
  
      <div style={S.card}>
  
        {/* Top Controls */}
        <div style={S.topRow}>
          <div style={S.searchWrap}>
            <span style={{ fontSize: 14, color: '#aaa' }}>🔍</span>
            <input
              type="text"
              placeholder="Search car group..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={S.search}
            />
          </div>
  
          <div style={S.formRow}>
            {editId && <span style={S.editingBadge}>Editing #{editId}</span>}
  
            <input
              type="text"
              placeholder="Car Group Name"
              value={name}
              onChange={e => setName(e.target.value)}
              style={S.input}
            />
  
            <input
              type="number"
              value={sequence}
              onChange={e => setSequence(parseInt(e.target.value) || 1)}
              style={{ ...S.input, width: 72 }}
            />
  
            {editId && (
              <button onClick={handleCancel} style={S.cancelBtn}>Cancel</button>
            )}
  
            <button onClick={handleSubmit} style={S.btn}>
              {saving ? '...' : editId ? 'Update' : 'Add'}
            </button>
          </div>
        </div>
  
        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr style={S.theadRow}>
                <th style={S.th}>#</th>
                <th style={S.th}>Group Name</th>
                <th style={S.th}>Sequence</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
  
            <tbody>
              {loading ? (
                <tr><td colSpan={4} style={S.emptyCell}>Loading…</td></tr>
              ) : data.map((row, i) => (
                <tr key={row.group_id}>
                  <td style={S.td}><span style={S.serial}>{(page - 1) * 10 + i + 1}</span></td>
                  <td style={S.td}><span style={S.groupChip}>{row.group_name}</span></td>
                  <td style={S.td}><span style={S.nameChip}>{row.sequence}</span></td>
                  <td style={{ ...S.td, textAlign: 'right' }}>
                    <button onClick={() => handleEdit(row)} style={S.editBtn}>Edit</button>
                    <button onClick={() => handleDelete(row.group_id)} style={S.delBtn}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
  
      </div>
  
    </Layout>
  );
}

/* ─── Styles ─── */
const S = {
  root: {
    display: 'flex', minHeight: '100vh',
    background: '#f4f6fb', fontFamily: "'DM Sans','Segoe UI',sans-serif"
  },

  /* Sidebar */
  // sidebar: {
  //   background: '#1a1d2e', color: '#fff', display: 'flex', flexDirection: 'column',
  //   position: 'fixed', height: '100vh', transition: 'width 0.25s', overflow: 'hidden', zIndex: 100
  // },
  // sideTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 16px 16px' },
  // logoWrap: { display: 'flex', alignItems: 'center', gap: 10 },
  // logoIcon: {
  //   width: 36, height: 36, background: 'linear-gradient(135deg,#6C63FF,#43BCCD)',
  //   borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
  //   fontSize: 18, flexShrink: 0
  // },
  // logoText: { fontSize: 18, fontWeight: 800, letterSpacing: '-0.5px', whiteSpace: 'nowrap' },
  // collapseBtn: {
  //   background: 'rgba(255,255,255,0.08)', border: 'none', color: '#aaa',
  //   borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 11, flexShrink: 0
  // },
  // navItem: {
  //   display: 'flex', alignItems: 'center', gap: 12,
  //   paddingTop: '11px', paddingBottom: '11px', paddingLeft: '16px', paddingRight: '16px',
  //   cursor: 'pointer', borderRadius: 10, margin: '2px 10px',
  //   color: '#aaa', fontSize: 14, transition: 'all 0.15s', whiteSpace: 'nowrap'
  // },
  // navActive: {
  //   background: 'rgba(99,179,237,0.12)', color: '#63b3ed',
  //   borderLeft: '3px solid #63b3ed', paddingLeft: '13px'
  // },
  // navIcon: { fontSize: 16, flexShrink: 0 },
  // navLabel: { fontWeight: 500 },
  // sideBottom: { padding: '16px', borderTop: '1px solid rgba(255,255,255,0.07)' },
  // userChip: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 },
  // avatar: {
  //   width: 36, height: 36, background: 'linear-gradient(135deg,#6C63FF,#43BCCD)',
  //   borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
  //   fontWeight: 800, fontSize: 15, flexShrink: 0
  // },
  // userName: { fontSize: 13, fontWeight: 700, color: '#fff' },
  // userRole: { fontSize: 11, color: '#777' },
  // logoutBtn: {
  //   width: '100%', background: 'rgba(255,100,100,0.12)', border: '1px solid rgba(255,100,100,0.2)',
  //   color: '#ff6b6b', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 13,
  //   display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit'
  // },
  serial:     { background: '#f3f2ff', color: '#6C63FF', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700 },
  nameChip:   { background: '#f0fff6', color: '#1a9e5c', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 },
  groupChip:  { background: '#f0f8ff', color: '#1a6eb5', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 },
  stockChip:  { background: '#fff3f0', color: '#d45700', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700 },
  badge:      { borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 },
  /* Main */
  main: { flex: 1, padding: '28px 28px 40px', minWidth: 0, transition: 'margin-left 0.25s' },

  /* Header */
  header: { marginBottom: 20 },
  title: { margin: 0, fontSize: 26, fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' },
  subtitle: { margin: '4px 0 0', fontSize: 13, color: '#999' },

  /* Card */
  card: {
    background: '#fff', borderRadius: 16,
    boxShadow: '0 2px 16px rgba(0,0,0,0.06)', overflow: 'hidden'
  },

  /* Top row */
  topRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    flexWrap: 'wrap', gap: 12, padding: '18px 20px',
    borderBottom: '1px solid #f0f0f0'
  },
  searchWrap: {
    display: 'flex', alignItems: 'center', gap: 8,
    background: '#f8f8f8', border: '1px solid #e8e8e8',
    borderRadius: 10, padding: '8px 14px', minWidth: 220
  },
  search: {
    border: 'none', outline: 'none', fontSize: 13,
    background: 'transparent', fontFamily: 'inherit', color: '#333', flex: 1
  },
  clearBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: 12, padding: 0 },
  formRow: { display: 'flex', alignItems: 'center', gap: 8 },
  editingBadge: {
    fontSize: 11, fontWeight: 700, background: '#f3f2ff',
    color: '#6C63FF', borderRadius: 20, padding: '3px 10px'
  },
  input: {
    padding: '9px 14px', border: '1.5px solid #e0e0e0', borderRadius: 10,
    fontSize: 13, fontFamily: 'inherit', color: '#333', outline: 'none', width: 200
  },
  cancelBtn: {
    padding: '9px 16px', background: '#f5f5f5', color: '#666',
    border: 'none', borderRadius: 10, cursor: 'pointer',
    fontSize: 13, fontWeight: 600, fontFamily: 'inherit'
  },
  btn: {
    padding: '9px 20px', background: 'linear-gradient(135deg,#6C63FF,#43BCCD)',
    color: '#fff', border: 'none', borderRadius: 10,
    cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit'
  },

  /* Table */
  table: { width: '100%', borderCollapse: 'collapse' },
  theadRow: { background: '#fafafa' },
  th: {
    padding: '12px 16px', fontSize: 11, fontWeight: 700, color: '#999',
    textAlign: 'left', borderBottom: '2px solid #f0f0f0',
    textTransform: 'uppercase', letterSpacing: '0.5px'
  },
  tr: { transition: 'background 0.12s' },
  td: { padding: '13px 16px', fontSize: 13, color: '#333', borderBottom: '1px solid #f8f8f8' },
  emptyCell: { textAlign: 'center', padding: 40, color: '#bbb', fontSize: 14 },
  serial: {
    background: '#f3f2ff', color: '#6C63FF',
    borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700
  },
  nameChip: {
    background: '#f0fff6', color: '#1a9e5c',
    borderRadius: 20, padding: '4px 14px', fontSize: 13, fontWeight: 600
  },
  seqChip: {
    background: '#f0f8ff', color: '#1a6eb5',
    borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700
  },
  editBtn: {
    marginRight: 6, background: '#f3f2ff', color: '#6C63FF',
    border: '1px solid #d4d0ff', padding: '5px 14px',
    borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit'
  },
  delBtn: {
    background: '#fff2f5', color: '#FF6584',
    border: '1px solid #ffd0d8', padding: '5px 14px',
    borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit'
  },

  /* Pagination */
  pagination: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 20px', borderTop: '1px solid #f0f0f0'
  },
  paginInfo: { fontSize: 12, color: '#999' },
  pgBtn: {
    padding: '5px 14px', background: '#f3f2ff', color: '#6C63FF',
    border: '1px solid #d4d0ff', borderRadius: 8, cursor: 'pointer',
    fontSize: 12, fontWeight: 600, fontFamily: 'inherit'
  },
};
